import React from "react";
import { AGGREGATE_FNS, canAggregate } from "./helpers";

/**
 * Summarise controls: group rows by one or more fields and compute aggregates
 * over them. With no aggregation the query returns plain rows; adding one turns
 * the preview into a grouped summary.
 * @param {object} props
 * @param {Array<object>} props.fields the dataset's field metadata.
 * @param {Array<object>} props.aggregations the aggregation rows (each with `_id`).
 * @param {string[]} props.groupBy selected group-by field keys.
 * @param {() => void} props.onAddAggregation adds an aggregation row.
 * @param {(index: number, patch: object) => void} props.onChangeAggregation updates a row.
 * @param {(index: number) => void} props.onRemoveAggregation removes a row.
 * @param {(key: string) => void} props.onToggleGroupBy toggles a group-by field.
 * @returns {JSX.Element}
 */
export default function SummaryBuilder({
  fields,
  aggregations,
  groupBy,
  onAddAggregation,
  onChangeAggregation,
  onRemoveAggregation,
  onToggleGroupBy,
}) {
  const groupable = fields.filter((f) => f.groupable);

  return (
    <div className="qb-field">
      <span className="qb-label">Summarise</span>

      {aggregations.length === 0 && (
        <p className="qb-muted">
          No summary — the preview returns individual rows.
        </p>
      )}

      {aggregations.map((agg, index) => (
        <div className="qb-filter-row" key={agg._id}>
          <select
            className="qb-select"
            aria-label="Aggregate function"
            value={agg.fn}
            onChange={(e) => onChangeAggregation(index, { fn: e.target.value })}
          >
            {AGGREGATE_FNS.map((fn) => (
              <option key={fn.value} value={fn.value}>
                {fn.label}
              </option>
            ))}
          </select>
          <select
            className="qb-select"
            aria-label="Aggregate field"
            value={agg.field}
            onChange={(e) =>
              onChangeAggregation(index, { field: e.target.value })
            }
          >
            <option value="">Select…</option>
            {fields
              .filter((f) => canAggregate(f, agg.fn))
              .map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
          </select>
          <button
            type="button"
            className="qb-icon-btn"
            aria-label="Remove aggregation"
            onClick={() => onRemoveAggregation(index)}
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        className="qb-btn qb-btn-ghost"
        onClick={onAddAggregation}
      >
        + Add aggregation
      </button>

      {aggregations.length > 0 && (
        <div className="qb-groupby">
          <span className="qb-label">Group by</span>
          {groupable.length === 0 ? (
            <p className="qb-muted">No groupable fields in this dataset.</p>
          ) : (
            <div className="qb-checkbox-grid">
              {groupable.map((field) => (
                <label key={field.key} className="qb-checkbox">
                  <input
                    type="checkbox"
                    checked={groupBy.includes(field.key)}
                    onChange={() => onToggleGroupBy(field.key)}
                  />
                  <span>{field.label}</span>
                </label>
              ))}
            </div>
          )}
          <p className="qb-muted">
            {groupBy.length === 0
              ? "No grouping — one total row for the whole result."
              : "Selected fields become the rows; aggregates become the columns."}
          </p>
        </div>
      )}
    </div>
  );
}
